import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, MultiSelect, NumberInput, Radio, SimpleGrid, Stack, Switch, TextInput } from '@mantine/core';
import { useTransportSurcharges } from '@/hooks/useBestOffers';
import { addDaysIso, tomorrowIso } from '@/lib/date';
import { fieldError, sellFieldId } from './sellFormFields';
import {
  SCALES_RANGE,
  STORAGE_PRICE_RANGE,
  VEHICLE_HEIGHT_RANGE,
  rateRange,
  type SellField,
  type SellFieldProblem,
  type SellFormState,
} from './sellFormState';
import classes from './LoadingConditionsFields.module.css';

const NO_PROBLEMS: ReadonlyMap<SellField, SellFieldProblem> = new Map();

interface LoadingConditionsFieldsProps {
  readonly state: SellFormState;
  readonly set: (patch: Partial<SellFormState>) => void;
  readonly shipmentModes: string[];
  /** Ошибки дат (i18n-ключи), вычислены в форме (зависят от объёма). */
  readonly dateErrors: { startDate?: string; endDate?: string };
  /** Проблемы полей формы: значение вне диапазона отмечается сразу, пустое обязательное — после попытки отправки. */
  readonly problems?: ReadonlyMap<SellField, SellFieldProblem>;
  /** Была ли попытка отправки. */
  readonly attempted?: boolean;
}

// Условия погрузки и хранения. Контролируемый компонент над срезом состояния формы. Четыре группы с подзаголовками
// («Сроки», «Отгрузка», «Оборудование», «Хранение»); выбор режима — Radio (все варианты видны), «да/нет» — Switch,
// поле, которое включает тумблер, стоит под ним. Даты — нативный type="date" (ISO), время — type="time" (HH:mm).
// Числа не подменяются при уходе с поля: значение вне диапазона остаётся с видимой ошибкой, пока его не исправят.
export default function LoadingConditionsFields({
  state,
  set,
  shipmentModes,
  dateErrors,
  problems = NO_PROBLEMS,
  attempted = false,
}: LoadingConditionsFieldsProps) {
  const { t } = useTranslation();
  const ids = { timing: useId(), shipment: useId(), equipment: useId(), storage: useId() };
  // Ставки надбавок — для подписей «выключение +X ₽/т·км». Пока не загружены, подписи не показываем.
  const { data: surcharges } = useTransportSurcharges();
  const exact = state.startMode === 'EXACT_DATE';
  const minStart = tomorrowIso();
  const minEnd = state.startDate ? addDaysIso(state.startDate, 1) : minStart;
  const error = (field: SellField, range?: Parameters<typeof fieldError>[4]) => fieldError(problems, field, attempted, t, range);
  const describeSwitch = { description: { color: 'var(--mantine-color-gray-7)' } };

  return (
    <Stack gap={32}>
      <section role="group" aria-labelledby={ids.timing} className={classes.group}>
        <h3 id={ids.timing} className={classes.title}>
          {t('app.sell.form.loading.groupTiming')}
        </h3>
        <Radio.Group
          label={t('app.sell.form.loading.startMode')}
          value={state.startMode}
          onChange={(value) => set({ startMode: value as SellFormState['startMode'] })}
        >
          <Group gap="lg" mt={6}>
            <Radio value="NO_DATE" label={t('app.sell.form.loading.modeNoDate')} />
            <Radio value="EXACT_DATE" label={t('app.sell.form.loading.modeExact')} />
            <Radio value="N_DAYS_AFTER_PAYMENT" label={t('app.sell.form.loading.modeAfterPayment')} />
          </Group>
        </Radio.Group>

        {exact ? (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              id={sellFieldId('startDate')}
              type="date"
              required
              label={t('app.sell.form.loading.startDate')}
              value={state.startDate}
              min={minStart}
              onChange={(e) => set({ startDate: e.currentTarget.value })}
              error={dateErrors.startDate ? t(dateErrors.startDate) : error('startDate')}
            />
            <TextInput
              id={sellFieldId('endDate')}
              type="date"
              label={t('app.sell.form.loading.endDate')}
              value={state.endDate}
              min={minEnd}
              onChange={(e) => set({ endDate: e.currentTarget.value })}
              error={dateErrors.endDate ? t(dateErrors.endDate) : undefined}
            />
          </SimpleGrid>
        ) : null}

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <TextInput
            type="time"
            label={t('app.sell.form.loading.startTime')}
            value={state.startTime}
            onChange={(e) => set({ startTime: e.currentTarget.value })}
          />
          <TextInput
            type="time"
            label={t('app.sell.form.loading.endTime')}
            value={state.endTime}
            onChange={(e) => set({ endTime: e.currentTarget.value })}
          />
        </SimpleGrid>
      </section>

      <section role="group" aria-labelledby={ids.shipment} className={classes.group}>
        <h3 id={ids.shipment} className={classes.title}>
          {t('app.sell.form.loading.groupShipment')}
        </h3>
        <MultiSelect
          id={sellFieldId('shipmentModes')}
          required
          label={t('app.sell.form.loading.modes')}
          placeholder={state.shipmentModes.length ? undefined : t('app.sell.form.loading.modesPlaceholder')}
          data={shipmentModes}
          value={state.shipmentModes}
          onChange={(value) => set({ shipmentModes: value })}
          searchable
          error={error('shipmentModes')}
        />

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          {/* Суточная отгрузка: число и под ним единица. На телефоне поле на всю ширину, единица не обрезается. */}
          <Stack gap={8}>
            <NumberInput
              id={sellFieldId('rate')}
              required
              label={t('app.sell.form.loading.rateValue')}
              value={state.rate}
              onChange={(value) => set({ rate: value === '' ? '' : Number(value) })}
              allowNegative={false}
              decimalScale={state.rateType === 'TS' ? 0 : 1}
              clampBehavior="none"
              error={error('rate', rateRange(state.rateType))}
            />
            <Radio.Group
              value={state.rateType}
              onChange={(value) => set({ rateType: value as SellFormState['rateType'] })}
              aria-label={t('app.sell.form.loading.rateType')}
            >
              <Group gap="lg">
                <Radio value="ton" label={t('app.sell.form.loading.rateTon')} />
                <Radio value="TS" label={t('app.sell.form.loading.rateTS')} />
              </Group>
            </Radio.Group>
          </Stack>
          <NumberInput
            id={sellFieldId('vehicleHeight')}
            required
            label={t('app.sell.form.loading.vehicleHeight')}
            value={state.vehicleHeight}
            onChange={(value) => set({ vehicleHeight: value === '' ? '' : Number(value) })}
            allowNegative={false}
            decimalScale={1}
            clampBehavior="none"
            error={error('vehicleHeight', VEHICLE_HEIGHT_RANGE)}
          />
        </SimpleGrid>
      </section>

      {/* Оборудование базиса влияет на надбавки к перевозке и на ориентир цены выше. По умолчанию всё включено,
          надбавок нет; выключение добавляет надбавку и снижает лучшую цену. */}
      <section role="group" aria-labelledby={ids.equipment} className={classes.group}>
        <h3 id={ids.equipment} className={classes.title}>
          {t('app.sell.form.loading.groupEquipment')}
        </h3>
        <Switch
          checked={state.hasScales}
          onChange={(e) => set({ hasScales: e.currentTarget.checked })}
          label={t('app.sell.form.loading.scalesSwitch')}
          description={surcharges ? t('app.sell.form.loading.surchargeScales', { rate: surcharges.noScalesRate }) : undefined}
          styles={describeSwitch}
        />
        {/* Длина весов стоит под тумблером и неактивна при выключенных весах: разметка не прыгает. */}
        <NumberInput
          id={sellFieldId('scalesLength')}
          className={classes.dependent}
          required={state.hasScales}
          label={t('app.sell.form.loading.scalesLength')}
          value={state.scalesLength}
          onChange={(value) => set({ scalesLength: value === '' ? '' : Number(value) })}
          disabled={!state.hasScales}
          allowNegative={false}
          decimalScale={1}
          clampBehavior="none"
          error={state.hasScales ? error('scalesLength', SCALES_RANGE) : undefined}
        />
        <Switch
          checked={state.trawls}
          onChange={(e) => set({ trawls: e.currentTarget.checked })}
          label={t('app.sell.form.loading.trawls')}
          description={
            surcharges
              ? t('app.sell.form.loading.surchargeTrawls', { rate: surcharges.noTralRate, km: surcharges.minDistanceKm })
              : undefined
          }
          styles={describeSwitch}
        />
        <Switch
          checked={state.trailers}
          onChange={(e) => set({ trailers: e.currentTarget.checked })}
          label={t('app.sell.form.loading.trailers')}
          description={
            surcharges
              ? t('app.sell.form.loading.surchargeTrailers', {
                  rate: surcharges.noDumpTruckRate,
                  km: surcharges.minDistanceKm,
                })
              : undefined
          }
          styles={describeSwitch}
        />
      </section>

      <section role="group" aria-labelledby={ids.storage} className={classes.group}>
        <h3 id={ids.storage} className={classes.title}>
          {t('app.sell.form.loading.storage')}
        </h3>
        <Switch
          checked={state.storageAvailable}
          onChange={(e) => set({ storageAvailable: e.currentTarget.checked })}
          label={t('app.sell.form.loading.storageYes')}
        />
        <NumberInput
          id={sellFieldId('storagePrice')}
          className={classes.dependent}
          required={state.storageAvailable}
          label={t('app.sell.form.loading.storagePrice')}
          value={state.storagePrice}
          onChange={(value) => set({ storagePrice: value === '' ? '' : Number(value) })}
          disabled={!state.storageAvailable}
          allowNegative={false}
          decimalScale={2}
          clampBehavior="none"
          error={state.storageAvailable ? error('storagePrice', STORAGE_PRICE_RANGE) : undefined}
        />
      </section>
    </Stack>
  );
}
